import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ListingCalendarService } from './listing-calendar.service';
import { ListingOptimizationSettingsService } from './listing-optimization-settings.service';

export interface PricingCalendarDay {
  date: string;
  price: number;
  minStay: number;
  available: boolean;
  event?: string;
  source: 'base' | 'custom' | 'pricelabs';
}

export interface DowAdjustment {
  day: 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';
  percent: number;          // -75..500
}

export interface PricingRule {
  thresholdDays: number;    // last-minute: within N days; far-out: beyond N days
  adjustmentPercent: number;
}

export interface OrphanDayRule {
  maxGapNights: number;
  adjustmentPercent: number;
}

export interface LosTier {
  minNights: number;
  maxNights: number | null;  // null = "+"
  adjustmentPercent: number;
}

export interface ExtraPersonFee {
  amountCents: number;
  feeType: 'fixed' | 'percent';
  triggerGuests: number;
}

export interface PricingCustomization {
  basePrice: number;
  minPrice: number;
  maxPrice: number;
  overrides: PricingOverride[];
  source: string;
  // Richer fields below mirror PriceLabs's customization surface.
  // Optional — no backend populates them; today they appear in mock data only.
  recommendedBasePrice?: number;
  dayOfWeekAdjustments?: DowAdjustment[];          // 7 entries Mon→Sun
  weeklyDiscountPercent?: number;
  monthlyDiscountPercent?: number;
  lastMinuteRule?: PricingRule;
  farOutRule?: PricingRule;
  orphanDayRule?: OrphanDayRule;
  lengthOfStayTiers?: LosTier[];
  extraPersonFee?: ExtraPersonFee;
}

export interface PricingOverride {
  date: string;
  price: number | null;
  minPrice: number | null;
  maxPrice: number | null;
  minStay: number | null;
}

export interface NeighborhoodComparison {
  avgAreaRate: number;
  avgAreaOccupancy: number;
  avgAreaRevenue: number;
  avgListingRate: number;
  occupancyRate: number;
  monthlyRevenue: number;
  bedrooms: number;
  source: string;
}

interface NeighborhoodResponse {
  avg_area_rate: number;
  avg_area_occupancy: number;
  avg_area_revenue: number;
  avg_listing_rate: number;
  occupancy_rate: number;
  monthly_revenue: number;
  bedrooms: number;
  source: string;
}

/**
 * Legacy facade for the marketing Pricing tab components. gotidy #8271 REMOVED
 * the old `address-pricing` calendar/customization endpoints (404 on sb3), so
 * this now adapts the same public surface onto their replacements:
 * `listing-calendar` (reads), `address-settings-by-date` (per-date overrides)
 * and the optimizer-category address-settings rows (account defaults). Only
 * `GET address-pricing/neighborhood` survives from the old namespace.
 * New code should use ListingCalendarService directly.
 */
@Injectable({ providedIn: 'root' })
export class MarketingPricingService {

  private readonly http = inject(HttpClient);
  private readonly listingCalendar = inject(ListingCalendarService);
  private readonly listingSettings = inject(ListingOptimizationSettingsService);
  private readonly neighborhoodUrl = `${environment.api_url}api/v1/customer/address-pricing/neighborhood`;

  async getCalendarData(addressId: number, dateFrom?: string, dateTo?: string): Promise<PricingCalendarDay[]> {
    const days = await this.listingCalendar.getMonth(addressId, dateFrom, dateTo);
    return days.map(day => ({
      date: day.date,
      price: day.price ?? 0,
      minStay: day.minStay ?? 1,
      // The old API's single `available` flag split into is_blocked + is_booked.
      available: !day.isBlocked && !day.isBooked,
      source: 'pricelabs' as const,
    }));
  }

  async getCustomization(addressId: number): Promise<PricingCustomization> {
    const [defaults, overrides] = await Promise.all([
      this.listingSettings.getOptimizerDefaults(addressId),
      this.listingCalendar.getDateSettings(addressId),
    ]);
    return {
      basePrice: defaults.basePrice ?? 0,
      minPrice: defaults.minPrice ?? 0,
      maxPrice: defaults.maxPrice ?? 0,
      overrides: overrides.map(o => ({
        date: o.date,
        price: o.overridePrice,
        minPrice: o.minPrice,
        maxPrice: o.maxPrice,
        minStay: o.minStay,
      })),
      source: 'tidy',
    };
  }

  async getNeighborhoodData(addressId: number): Promise<NeighborhoodComparison> {
    const response = await firstValueFrom(
      this.http.get<NeighborhoodResponse>(this.neighborhoodUrl, {
        params: { address_id: addressId }
      })
    );

    return {
      avgAreaRate: response.avg_area_rate,
      avgAreaOccupancy: response.avg_area_occupancy,
      avgAreaRevenue: response.avg_area_revenue,
      avgListingRate: response.avg_listing_rate,
      occupancyRate: response.occupancy_rate,
      monthlyRevenue: response.monthly_revenue,
      bedrooms: response.bedrooms,
      source: response.source
    };
  }

  async setCustomization(addressId: number, date: string, overrides: {
    price?: number;
    minPrice?: number;
    maxPrice?: number;
    minStay?: number;
  }): Promise<void> {
    // Same semantics as before: only the provided fields are written. The new
    // API also accepts null to CLEAR a field, but this legacy surface never
    // sent nulls, so undefined fields are simply omitted (left unchanged).
    await this.listingCalendar.setDateSettings(addressId, date, date, {
      ...(overrides.price !== null && overrides.price !== undefined ? { overridePrice: overrides.price } : {}),
      ...(overrides.minPrice !== null && overrides.minPrice !== undefined ? { minPrice: overrides.minPrice } : {}),
      ...(overrides.maxPrice !== null && overrides.maxPrice !== undefined ? { maxPrice: overrides.maxPrice } : {}),
      ...(overrides.minStay !== null && overrides.minStay !== undefined ? { minStay: overrides.minStay } : {}),
    });
  }

  async deleteCustomization(addressId: number, date: string): Promise<void> {
    await this.listingCalendar.clearDateSettings(addressId, date);
  }
}
